import { type RefObject, useCallback, useEffect, useId, useRef, useState } from "react";
import { Button } from "primereact/button";

import { INTERNAL_ROUTES } from "../../../../constants/routes";

import S from "../UserNavBar.module.scss";

const SEEN_KEY = "myJourneyCalloutSeen";

// Closed without "Got it": stays hidden until the next page load, even though the
// logo remounts when the nav switches layouts (menubar vs. breadcrumbs).
let hiddenUntilReload = false;

interface MyJourneyCalloutProps {
  /** The logo button; focus returns here if the callout closes while focused. */
  logoRef: RefObject<HTMLButtonElement>;
}

/**
 * One-time callout under the nav logo telling users My Journey left the top nav
 * (SHRPA-4667). It renders right after the logo, so it's next in tab and reading
 * order, and it never takes focus on its own. Only "Got it" marks it seen for
 * good (per browser). Escape, clicking elsewhere, or tabbing past it just hide
 * it until the next page load, so it can't sit over focused page content.
 * Delete with the `show-my-journey-callout` flag.
 */
export const MyJourneyCallout = ({ logoRef }: MyJourneyCalloutProps) => {
  const [visible, setVisible] = useState(
    () => !hiddenUntilReload && !localStorage.getItem(SEEN_KEY)
  );
  const containerRef = useRef<HTMLDivElement>(null);
  const textId = useId();

  const hide = useCallback(() => {
    if (containerRef.current?.contains(document.activeElement)) {
      logoRef.current?.focus();
    }
    hiddenUntilReload = true;
    setVisible(false);
  }, [logoRef]);

  const acknowledge = () => {
    localStorage.setItem(SEEN_KEY, "true");
    hide();
  };

  useEffect(() => {
    if (!visible) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") hide();
    };
    const onPointerDown = (e: PointerEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) hide();
    };
    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("pointerdown", onPointerDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("pointerdown", onPointerDown);
    };
  }, [visible, hide]);

  if (!visible) return null;

  return (
    <div
      ref={containerRef}
      className={`${S.myJourneyCallout} body-sm`}
      data-testid="my-journey-callout"
      onBlur={e => {
        if (e.relatedTarget && !e.currentTarget.contains(e.relatedTarget)) hide();
      }}
    >
      <div id={textId}>
        <p className="m-0 font-semibold">Looking for {INTERNAL_ROUTES.Learn.name}?</p>
        <p className="mt-1 mb-3">
          It&apos;s no longer in the menu. Select the logo to get back to it anytime.
        </p>
      </div>
      <Button label="Got it" size="small" aria-describedby={textId} onClick={acknowledge} />
    </div>
  );
};
